import { IFocusTarget } from './form-focus-target'

/**
 * Per-path list mechanics shared by both platforms' FormFocusTargetProvider. A path maps
 * to a LIST (twins share a path) — see .claude/docs/ui2-architecture.md (Behavior helpers).
 * Liveness and ordering are per-platform, so callers pass those in.
 */

let counter = 0

export function nextFocusRegistrationId(): string {
  counter += 1
  return `ft-${counter}`
}

export function addRegistration<TRef>(
  list: Array<IFocusTarget<TRef>> | undefined,
  target: IFocusTarget<TRef>,
): Array<IFocusTarget<TRef>> {
  const id = target.registrationId
  const kept = (list ?? []).filter((entry) =>
    // An anonymous registration replaces the previous anonymous one on the path.
    id === undefined ? entry.registrationId !== undefined : entry.registrationId !== id,
  )
  kept.push(target)
  return kept
}

export function removeRegistration<TRef>(
  list: Array<IFocusTarget<TRef>> | undefined,
  registrationId?: string,
): Array<IFocusTarget<TRef>> {
  return (list ?? []).filter((entry) =>
    registrationId === undefined
      ? entry.registrationId !== undefined
      : entry.registrationId !== registrationId,
  )
}

interface IPickOptions<TRef> {
  isAlive?: (target: IFocusTarget<TRef>) => boolean
}

/**
 * Which of several registrants a path-addressed call acts on: errored first (focus
 * usually follows a validation failure), then any live one, then anything.
 */
export function pickFocusTarget<TRef>(
  list: Array<IFocusTarget<TRef>> | undefined,
  options: IPickOptions<TRef> = {},
): IFocusTarget<TRef> | undefined {
  const entries = list ?? []
  if (entries.length === 0) return undefined
  const isAlive = options.isAlive ?? (() => true)
  const live = entries.filter(isAlive)
  return live.find((entry) => entry.hasError?.()) ?? live[0] ?? entries[0]
}
